import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { authFetch, isAuthenticated } from '../utils/auth';
import { toast } from 'sonner';
import { MessageSquare, Star, Send, CheckCircle2, Clock } from 'lucide-react';

const CATEGORIES = [
  { value: 'general', label: 'General Feedback' },
  { value: 'collection', label: 'Pet Collection' },
  { value: 'portal', label: 'Portal Experience' },
  { value: 'products', label: 'Products & Rewards' },
  { value: 'suggestion', label: 'Suggestion' },
];

const Feedback = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    fetchHistory();
  }, [navigate]);

  const fetchHistory = async () => {
    try {
      const res = await authFetch('/api/feedback');
      if (res.ok) {
        const data = await res.json();
        setHistory(data.feedback || []);
      }
    } catch {} finally { setLoadingHistory(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error('Please enter your feedback'); return; }
    setSubmitting(true);
    try {
      const res = await authFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({ message, rating: rating || null, category }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        toast.success(data.message);
        fetchHistory();
      } else {
        toast.error(data.detail || 'Failed to submit');
      }
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setMessage('');
    setRating(0);
    setCategory('general');
    setSubmitted(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8" data-testid="feedback-page">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="text-4xl font-bold mb-2 text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Feedback
          </h1>
          <p className="text-muted-foreground text-lg">Help us improve our service</p>
        </motion.div>

        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
              <Card className="border-0 shadow-md">
                <CardContent className="pt-12 pb-12 text-center">
                  <motion.div
                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                    className="inline-flex p-4 rounded-full bg-[hsl(var(--success-light))] mb-6"
                  >
                    <CheckCircle2 className="h-12 w-12 text-[hsl(var(--success))]" />
                  </motion.div>
                  <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>Thank You!</h2>
                  <p className="text-muted-foreground mb-6">Your feedback has been sent to the HOLONS team.</p>
                  <Button onClick={resetForm} variant="outline">Submit More Feedback</Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Share Your Thoughts</CardTitle>
                      <CardDescription>Your feedback helps us serve you better</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Category */}
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <div className="flex flex-wrap gap-2">
                        {CATEGORIES.map(cat => (
                          <button
                            key={cat.value}
                            type="button"
                            onClick={() => setCategory(cat.value)}
                            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                              category === cat.value
                                ? 'border-primary bg-primary/5 text-primary ring-1 ring-primary/30'
                                : 'border-border text-muted-foreground hover:border-primary/30'
                            }`}
                            data-testid={`category-${cat.value}`}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="space-y-2">
                      <Label>How would you rate your experience?</Label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            className="p-1 cursor-pointer transition-transform hover:scale-110"
                            data-testid={`star-${star}`}
                          >
                            <Star
                              className={`h-8 w-8 transition-colors ${
                                star <= (hoverRating || rating)
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-border'
                              }`}
                            />
                          </button>
                        ))}
                        {rating > 0 && (
                          <span className="ml-2 text-sm text-muted-foreground self-center">
                            {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="feedback-msg">Your Feedback *</Label>
                      <textarea
                        id="feedback-msg"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tell us what's working well, what could be improved, or share any suggestions..."
                        rows={5}
                        className="w-full border border-border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                        data-testid="feedback-message"
                      />
                    </div>

                    <Button type="submit" disabled={submitting || !message.trim()} className="w-full gap-2" data-testid="feedback-submit">
                      {submitting ? <Clock className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {submitting ? 'Sending...' : 'Submit Feedback'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Previous Feedback */}
        {history.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>Your Previous Feedback</h2>
            <div className="space-y-3">
              {history.map((item, i) => (
                <Card key={`${item.category}-${item.created_at}-${i}`} className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                            {CATEGORIES.find(c => c.value === item.category)?.label || 'General'}
                          </span>
                          {item.rating && (
                            <div className="flex gap-0.5">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`h-3 w-3 ${s <= item.rating ? 'fill-amber-400 text-amber-400' : 'text-border'}`} />
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-sm">{item.message}</p>
                      </div>
                      <span className="text-xs text-muted-foreground flex-shrink-0">{formatDate(item.created_at)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

export default Feedback;
